import { useState } from 'react'
import * as stylex from '@stylexjs/stylex'

import {
  ACCESSORIES,
  AVATAR_COLORS,
  cycleAvatarValue,
  EYE_STYLES,
  FACIAL_HAIR_STYLES,
  FACE_SHAPES,
  generatedAvatarConfig,
  HAIR_STYLES,
  MOUTH_STYLES,
  parseAvatarConfig,
  randomAvatarConfig,
  serializeAvatarConfig,
  type AvatarConfig,
  type AvatarPart,
} from '../lib/avatar'
import AvatarArtwork from './AvatarArtwork'

const styles = stylex.create({
  root: {
    display: 'grid',
    gap: '16px',
    borderRadius: '1rem',
    borderWidth: 1,
    borderColor: '#e1e8df',
    backgroundColor: '#f8fbf7',
    padding: '16px',
    '@media (min-width: 680px)': {
      gridTemplateColumns: '180px minmax(0, 1fr)',
      alignItems: 'start',
    },
  },
  previewColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
  },
  preview: {
    height: '144px',
    width: '144px',
    overflow: 'hidden',
    borderRadius: '9999px',
    borderWidth: 5,
    borderColor: '#fff',
    backgroundColor: '#e8efe6',
    boxShadow: '0 7px 22px rgba(48,70,51,.12)',
  },
  hint: {
    maxWidth: '170px',
    textAlign: 'center',
    fontSize: '11px',
    lineHeight: 1.45,
    color: '#748077',
  },
  quickActions: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '6px',
  },
  smallButton: {
    minHeight: '30px',
    borderRadius: '0.5rem',
    borderWidth: 1,
    borderColor: '#d9e4d7',
    backgroundColor: '#fff',
    paddingInline: '9px',
    fontSize: '11px',
    fontWeight: 600,
    color: '#56705d',
    ':hover': { backgroundColor: '#f1f7ef' },
  },
  editor: { minWidth: 0 },
  tabs: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  tab: {
    minHeight: '32px',
    borderRadius: '9999px',
    borderWidth: 1,
    borderColor: '#dce6da',
    backgroundColor: '#fff',
    paddingInline: '10px',
    fontSize: '11px',
    fontWeight: 600,
    color: '#647267',
    ':hover': { borderColor: '#b9cfb7', backgroundColor: '#f5faf3' },
  },
  tabActive: {
    borderColor: '#7fb28a',
    backgroundColor: '#eaf5e8',
    color: '#347a49',
  },
  panel: {
    marginTop: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  label: {
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '.06em',
    color: '#7a867d',
  },
  choices: { display: 'flex', flexWrap: 'wrap', gap: '7px' },
  choice: {
    minHeight: '34px',
    borderRadius: '0.625rem',
    borderWidth: 1,
    borderColor: '#dce6da',
    backgroundColor: '#fff',
    paddingInline: '10px',
    fontSize: '11px',
    fontWeight: 600,
    color: '#59685e',
    ':hover': { borderColor: '#a9c4a8' },
  },
  choiceActive: {
    borderColor: '#5b9a6c',
    backgroundColor: '#edf7eb',
    color: '#347a49',
  },
  swatch: {
    height: '30px',
    width: '30px',
    borderRadius: '9999px',
    borderWidth: 3,
    borderColor: '#fff',
    boxShadow: '0 0 0 1px #d7e0d5',
  },
  swatchActive: { boxShadow: '0 0 0 2px #4b8d5c' },
})

type Props = {
  name: string
  value?: string
  onChange: (value: string) => void
}

const partLabels: Array<{ part: AvatarPart; label: string }> = [
  { part: 'face', label: 'Face' },
  { part: 'hair', label: 'Hair' },
  { part: 'eyes', label: 'Eyes' },
  { part: 'mouth', label: 'Mouth' },
  { part: 'facialHair', label: 'Beard' },
  { part: 'accessory', label: 'Glasses' },
  { part: 'background', label: 'Background' },
  { part: 'shirt', label: 'Shirt' },
]

function titleCase(value: string): string {
  return value.replace(/-/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

export default function AvatarEditor({ name, value, onChange }: Props) {
  const [config, setConfig] = useState<AvatarConfig>(() => parseAvatarConfig(value, name))
  const [part, setPart] = useState<AvatarPart>('hair')

  function commit(next: AvatarConfig) {
    setConfig(next)
    onChange(serializeAvatarConfig(next))
  }

  function update<K extends keyof AvatarConfig>(key: K, nextValue: AvatarConfig[K]) {
    commit({ ...config, [key]: nextValue })
  }

  function clickPart(clicked: AvatarPart) {
    setPart(clicked)
    if (clicked === 'hair') update('hairColor', cycleAvatarValue(AVATAR_COLORS.hair, config.hairColor))
    if (clicked === 'face') update('skin', cycleAvatarValue(AVATAR_COLORS.skin, config.skin))
    if (clicked === 'eyes') update('eyeColor', cycleAvatarValue(AVATAR_COLORS.eyes, config.eyeColor))
    if (clicked === 'mouth') update('mouthStyle', cycleAvatarValue(MOUTH_STYLES, config.mouthStyle))
    if (clicked === 'facialHair') update('facialHair', cycleAvatarValue(FACIAL_HAIR_STYLES, config.facialHair))
    if (clicked === 'accessory') update('accessory', cycleAvatarValue(ACCESSORIES, config.accessory))
    if (clicked === 'background') update('background', cycleAvatarValue(AVATAR_COLORS.background, config.background))
    if (clicked === 'shirt') update('shirtColor', cycleAvatarValue(AVATAR_COLORS.shirt, config.shirtColor))
  }

  function colorChoices(values: readonly string[], current: string, onSelect: (value: string) => void) {
    return (
      <div {...stylex.props(styles.choices)}>
        {values.map((color) => (
          <button
            aria-label={`Use ${color}`}
            aria-pressed={current === color}
            {...stylex.props(styles.swatch, current === color && styles.swatchActive)}
            key={color}
            onClick={() => onSelect(color)}
            style={{ backgroundColor: color }}
            type="button"
          />
        ))}
      </div>
    )
  }

  function textChoices<T extends string>(values: readonly T[], current: T, onSelect: (value: T) => void) {
    return (
      <div {...stylex.props(styles.choices)}>
        {values.map((item) => (
          <button
            aria-pressed={current === item}
            {...stylex.props(styles.choice, current === item && styles.choiceActive)}
            key={item}
            onClick={() => onSelect(item)}
            type="button"
          >
            {titleCase(item)}
          </button>
        ))}
      </div>
    )
  }

  return (
    <div {...stylex.props(styles.root)}>
      <div {...stylex.props(styles.previewColumn)}>
        <svg aria-label="Avatar preview. Click a part to change it." {...stylex.props(styles.preview)} role="img" viewBox="0 0 48 48">
          <AvatarArtwork config={config} onPartClick={clickPart} />
        </svg>
        <p {...stylex.props(styles.hint)}>Click the hair, face, eyes, clothing, or background to quickly cycle it.</p>
        <div {...stylex.props(styles.quickActions)}>
          <button {...stylex.props(styles.smallButton)} onClick={() => commit(randomAvatarConfig(name))} type="button">Randomize</button>
          <button {...stylex.props(styles.smallButton)} onClick={() => commit(generatedAvatarConfig(name))} type="button">Reset</button>
        </div>
      </div>

      <div {...stylex.props(styles.editor)}>
        <div {...stylex.props(styles.tabs)}>
          {partLabels.map((item) => (
            <button
              aria-pressed={part === item.part}
              {...stylex.props(styles.tab, part === item.part && styles.tabActive)}
              key={item.part}
              onClick={() => setPart(item.part)}
              type="button"
            >
              {item.label}
            </button>
          ))}
        </div>

        <div {...stylex.props(styles.panel)}>
          {part === 'face' && <><span {...stylex.props(styles.label)}>Face shape</span>{textChoices(FACE_SHAPES, config.faceShape, (value) => update('faceShape', value))}<span {...stylex.props(styles.label)}>Skin tone</span>{colorChoices(AVATAR_COLORS.skin, config.skin, (value) => update('skin', value))}</>}
          {part === 'hair' && <><span {...stylex.props(styles.label)}>Hair style</span>{textChoices(HAIR_STYLES, config.hairStyle, (value) => update('hairStyle', value))}<span {...stylex.props(styles.label)}>Hair color</span>{colorChoices(AVATAR_COLORS.hair, config.hairColor, (value) => update('hairColor', value))}</>}
          {part === 'eyes' && <><span {...stylex.props(styles.label)}>Eyes</span>{textChoices(EYE_STYLES, config.eyeStyle, (value) => update('eyeStyle', value))}<span {...stylex.props(styles.label)}>Eye color</span>{colorChoices(AVATAR_COLORS.eyes, config.eyeColor, (value) => update('eyeColor', value))}</>}
          {part === 'mouth' && <><span {...stylex.props(styles.label)}>Expression</span>{textChoices(MOUTH_STYLES, config.mouthStyle, (value) => update('mouthStyle', value))}</>}
          {part === 'facialHair' && <><span {...stylex.props(styles.label)}>Facial hair</span>{textChoices(FACIAL_HAIR_STYLES, config.facialHair, (value) => update('facialHair', value))}<span {...stylex.props(styles.label)}>Color</span>{colorChoices(AVATAR_COLORS.hair, config.hairColor, (value) => update('hairColor', value))}</>}
          {part === 'accessory' && <><span {...stylex.props(styles.label)}>Accessories</span>{textChoices(ACCESSORIES, config.accessory, (value) => update('accessory', value))}</>}
          {part === 'background' && <><span {...stylex.props(styles.label)}>Background</span>{colorChoices(AVATAR_COLORS.background, config.background, (value) => update('background', value))}</>}
          {part === 'shirt' && <><span {...stylex.props(styles.label)}>Shirt color</span>{colorChoices(AVATAR_COLORS.shirt, config.shirtColor, (value) => update('shirtColor', value))}</>}
        </div>
      </div>
    </div>
  )
}
